import React from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

// v6 F-Invoicing F2 — Herbruikbare line-items editor voor drafts.
// v14p — "Dienst" toegevoegd als eerste + standaardtype voor nieuwe regels.
// Bestaande product/discount/option-waarden blijven bewaard (backward compat).
// Volledig gecontroleerd (controlled): parent bezit de state.
// Wanneer readOnly=true tonen we een leesbare tabel zonder inputs.
const VAT_RATES = [0, 6, 21];
const LINE_TYPES = [
  { value: "service", label: "Dienst" },
  { value: "product", label: "Product" },
  { value: "discount", label: "Korting" },
  { value: "option", label: "Optie" },
];

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" })
    .format(Number(n || 0));

export default function InvoiceLinesEditor({
  lines, onChange, readOnly = false, defaultVat = 21,
  quoteLinked = false, quoteNumber = null,
}) {
  // v14q — quote-linked drafts: BTW-percentage komt uit offerte, dus geen
  // dropdown per regel — enkel een read-only info-badge. Voorkomt dat gebruikers
  // per ongeluk de offerte-BTW overschrijven zonder de offerte zelf aan te passen.
  const vatLocked = quoteLinked && !readOnly;
  const setLine = (idx, patch) => {
    onChange(lines.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));
  };
  const removeLine = (idx) => onChange(lines.filter((_, i) => i !== idx));
  const addLine = () =>
    onChange([
      ...lines,
      {
        description: "",
        quantity: 1,
        unit: "fixed",
        unit_price_excl: 0,
        vat_rate: defaultVat,
        line_type: "service", // v14p — Dienst standaard voor nieuwe regels
        sort_order: lines.length,
      },
    ]);
  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= lines.length) return;
    const next = lines.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  const lineTotal = (ln) => {
    const raw = Number(ln.quantity || 0) * Number(ln.unit_price_excl || 0);
    return ln.line_type === "discount" ? -Math.abs(raw) : raw;
  };

  if (readOnly && lines.length === 0) {
    return (
      <div className="text-sm text-[#4F4F4D] italic py-4 text-center" data-testid="lines-empty-readonly">
        Geen factuurregels.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="invoice-lines-editor">
      {/* v14q — BTW-slot info wanneer factuur uit offerte komt */}
      {vatLocked && (
        <div
          className="text-[11px] text-[#4F4F4D] italic px-3 py-2 rounded-md bg-[#FAF9F6] border border-[#E5E2DC]"
          data-testid="vat-locked-notice"
        >
          BTW wordt overgenomen uit {quoteNumber ? `offerte ${quoteNumber}` : "de offerte"} en is niet aanpasbaar op regelniveau.
        </div>
      )}
      {/* Desktop tabel */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-[#4F4F4D] border-b border-[#E5E2DC]">
              <th className="pb-2 pr-2 w-8"></th>
              <th className="pb-2 pr-2">Omschrijving</th>
              <th className="pb-2 pr-2 w-20 text-right">Aantal</th>
              <th className="pb-2 pr-2 w-28 text-right">Prijs excl.</th>
              <th className="pb-2 pr-2 w-20 text-right">BTW</th>
              <th className="pb-2 pr-2 w-28">Type</th>
              <th className="pb-2 pr-2 w-28 text-right">Totaal excl.</th>
              {!readOnly && <th className="pb-2 w-20"></th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((ln, idx) => (
              <tr key={idx} className="border-b border-[#F0EFEA]" data-testid={`line-row-${idx}`}>
                <td className="py-2 pr-2 text-xs text-[#A8A7A0] font-mono">{idx + 1}</td>
                <td className="py-2 pr-2">
                  {readOnly ? (
                    <div className="text-[#2C2C2A]">{ln.description || "—"}</div>
                  ) : (
                    <Input
                      value={ln.description || ""}
                      onChange={(e) => setLine(idx, { description: e.target.value })}
                      placeholder="Omschrijving"
                      className="h-9 text-sm"
                      data-testid={`line-desc-${idx}`}
                    />
                  )}
                </td>
                <td className="py-2 pr-2 text-right">
                  {readOnly ? (
                    <span className="text-[#2C2C2A]">{Number(ln.quantity || 0)}</span>
                  ) : (
                    <Input
                      type="number" step="0.01" value={ln.quantity ?? 0}
                      onChange={(e) => setLine(idx, { quantity: parseFloat(e.target.value) || 0 })}
                      className="h-9 text-sm text-right"
                      data-testid={`line-qty-${idx}`}
                    />
                  )}
                </td>
                <td className="py-2 pr-2 text-right">
                  {readOnly ? (
                    <span className="text-[#2C2C2A]">{fmtEur(ln.unit_price_excl)}</span>
                  ) : (
                    <Input
                      type="number" step="0.01" value={ln.unit_price_excl ?? 0}
                      onChange={(e) => setLine(idx, { unit_price_excl: parseFloat(e.target.value) || 0 })}
                      className="h-9 text-sm text-right"
                      data-testid={`line-price-${idx}`}
                    />
                  )}
                </td>
                <td className="py-2 pr-2 text-right">
                  {readOnly ? (
                    <span className="text-[#2C2C2A]">{ln.vat_rate}%</span>
                  ) : vatLocked ? (
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-md bg-[#F0EFEA] text-xs text-[#4F4F4D] font-medium"
                      title={quoteNumber ? `uit offerte ${quoteNumber}` : "uit offerte"}
                      data-testid={`line-vat-locked-${idx}`}
                    >
                      {ln.vat_rate}%
                    </span>
                  ) : (
                    <Select value={String(ln.vat_rate)} onValueChange={(v) => setLine(idx, { vat_rate: parseInt(v, 10) })}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`line-vat-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td className="py-2 pr-2">
                  {readOnly ? (
                    <span className="text-[#4F4F4D] text-xs">{LINE_TYPES.find(t => t.value === ln.line_type)?.label || "Product"}</span>
                  ) : (
                    <Select value={ln.line_type || "product"} onValueChange={(v) => setLine(idx, { line_type: v })}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`line-type-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LINE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </td>
                <td className="py-2 pr-2 text-right font-medium text-[#2C2C2A]" data-testid={`line-total-${idx}`}>
                  {fmtEur(lineTotal(ln))}
                </td>
                {!readOnly && (
                  <td className="py-2 pr-2">
                    <div className="flex justify-end gap-0.5">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`line-up-${idx}`}>
                        <ArrowUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => move(idx, +1)} disabled={idx === lines.length - 1} data-testid={`line-down-${idx}`}>
                        <ArrowDown className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#C0392B]" onClick={() => removeLine(idx)} data-testid={`line-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobiele kaartlayout */}
      <div className="md:hidden space-y-3">
        {lines.map((ln, idx) => (
          <div key={idx} className="border border-[#E5E2DC] rounded-xl p-3 bg-white space-y-2" data-testid={`line-card-${idx}`}>
            <div className="flex items-center justify-between text-xs text-[#4F4F4D]">
              <span className="font-mono">Regel {idx + 1}</span>
              {!readOnly && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => move(idx, +1)} disabled={idx === lines.length - 1}>
                    <ArrowDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#C0392B]" onClick={() => removeLine(idx)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
            {readOnly ? (
              <div className="space-y-1">
                <div className="text-sm text-[#2C2C2A]">{ln.description || "—"}</div>
                <div className="text-xs text-[#4F4F4D]">
                  {Number(ln.quantity || 0)} × {fmtEur(ln.unit_price_excl)} · BTW {ln.vat_rate}%
                </div>
                <div className="text-right text-sm font-medium text-[#2C2C2A]">{fmtEur(lineTotal(ln))}</div>
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label className="text-xs text-[#4F4F4D] font-medium">Omschrijving</Label>
                  <Input
                    value={ln.description || ""}
                    onChange={(e) => setLine(idx, { description: e.target.value })}
                    placeholder="Bv. Schilderwerken living"
                    className="h-10"
                    data-testid={`line-desc-mobile-${idx}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#4F4F4D] font-medium">Aantal</Label>
                    <Input
                      type="number" step="0.01" value={ln.quantity ?? 0}
                      onChange={(e) => setLine(idx, { quantity: parseFloat(e.target.value) || 0 })}
                      className="h-10"
                      data-testid={`line-qty-mobile-${idx}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#4F4F4D] font-medium">Eenheidsprijs</Label>
                    <Input
                      type="number" step="0.01" value={ln.unit_price_excl ?? 0}
                      onChange={(e) => setLine(idx, { unit_price_excl: parseFloat(e.target.value) || 0 })}
                      className="h-10"
                      data-testid={`line-price-mobile-${idx}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-[#4F4F4D] font-medium">BTW</Label>
                    {vatLocked ? (
                      <div
                        className="h-10 flex items-center px-3 rounded-md bg-[#F0EFEA] text-sm text-[#4F4F4D] font-medium"
                        data-testid={`line-vat-locked-mobile-${idx}`}
                      >
                        {ln.vat_rate}%
                      </div>
                    ) : (
                      <Select value={String(ln.vat_rate)} onValueChange={(v) => setLine(idx, { vat_rate: parseInt(v, 10) })}>
                        <SelectTrigger className="h-10" data-testid={`line-vat-mobile-${idx}`}><SelectValue placeholder="BTW" /></SelectTrigger>
                        <SelectContent>{VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-[#4F4F4D] font-medium">Type</Label>
                    <Select value={ln.line_type || "product"} onValueChange={(v) => setLine(idx, { line_type: v })}>
                      <SelectTrigger className="h-10" data-testid={`line-type-mobile-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{LINE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-between items-center text-sm pt-1 border-t border-[#F0EFEA]">
                  <span className="text-[#4F4F4D]">Totaal excl.</span>
                  <span className="font-medium text-[#2C2C2A]">{fmtEur(lineTotal(ln))}</span>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <Button
          type="button" onClick={addLine} variant="outline"
          className="h-10 rounded-xl border-dashed border-[#E5E2DC] text-[#4F4F4D] hover:bg-[#F0EFEA]"
          data-testid="line-add-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Regel toevoegen
        </Button>
      )}
    </div>
  );
}
